/**
 * Tab Component
 * Provides accessible tab UI
 */
export class Tab {
  private element: HTMLElement;
  private tablist: HTMLElement;
  private tabs: NodeListOf<HTMLButtonElement>;
  private panels: NodeListOf<HTMLElement>;

  constructor(element: HTMLElement) {
    this.element = element;
    this.tablist = this.element.querySelector('[role="tablist"]') as HTMLElement;
    this.tabs = this.element.querySelectorAll('[role="tab"]') as NodeListOf<HTMLButtonElement>;
    this.panels = this.element.querySelectorAll('[role="tabpanel"]') as NodeListOf<HTMLElement>;

    if (!this.tablist || !this.tabs.length || !this.panels.length) {
      console.warn('[Tab] Required elements not found');
      return;
    }

    this.init();
  }

  /**
   * Initialize
   */
  private init(): void {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchTab(index);
      });

      tab.addEventListener('keydown', (e) => {
        this.handleKeydown(e, index);
      });
    });
  }

  /**
   * Switch tab
   */
  private switchTab(index: number): void {
    // Deactivate all tabs
    this.tabs.forEach((tab) => {
      tab.classList.remove('ms-c-color-taste__tab--active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    // Hide all panels
    this.panels.forEach((panel) => {
      panel.classList.remove('ms-c-color-taste__content--active');
      panel.setAttribute('hidden', '');
    });

    // Activate selected tab
    this.tabs[index].classList.add('ms-c-color-taste__tab--active');
    this.tabs[index].setAttribute('aria-selected', 'true');
    this.tabs[index].removeAttribute('tabindex');
    this.tabs[index].focus();

    // Show corresponding panel
    this.panels[index].classList.add('ms-c-color-taste__content--active');
    this.panels[index].removeAttribute('hidden');
  }

  /**
   * Handle keyboard navigation
   */
  private handleKeydown(e: KeyboardEvent, currentIndex: number): void {
    let newIndex: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        // Previous tab (loop to end)
        newIndex = currentIndex - 1;
        if (newIndex < 0) {
          newIndex = this.tabs.length - 1;
        }
        break;

      case 'ArrowRight':
        // Next tab (loop to start)
        newIndex = currentIndex + 1;
        if (newIndex >= this.tabs.length) {
          newIndex = 0;
        }
        break;

      case 'Home':
        // First tab
        newIndex = 0;
        break;

      case 'End':
        // Last tab
        newIndex = this.tabs.length - 1;
        break;
    }

    if (newIndex !== null) {
      e.preventDefault();
      this.switchTab(newIndex);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const tabElements = document.querySelectorAll('.ms-c-color-taste');
  tabElements.forEach((element) => {
    new Tab(element as HTMLElement);
  });
});
